import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Calendar, MapPin, Phone, Mail, Users, Clock, ChevronLeft, ChevronRight, Tent, Tag, Check, X } from "lucide-react";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isWeekend, subDays } from "date-fns";
import { toast } from "@/hooks/use-toast";
import MapEmbed from "@/components/MapEmbed";
import PublicReviewsSection from "@/components/reviews/PublicReviewsSection";

interface Tenant {
  id: string;
  name: string;
  slug: string;
  general_settings?: {
    email_logo_url: string | null;
    address_line1: string | null;
    address_line2: string | null;
    city: string | null;
    postal_code: string | null;
    contact_phone: string | null;
    contact_email: string | null;
    landing_page_description: string | null;
    latitude: number | null;
    longitude: number | null;
    next_season_active_from: string | null;
    what3words_address: string | null;
  }[];
}

interface Facility {
  id: string;
  name: string;
  description: string | null;
  capacity: number;
  price_per_night: number;
  next_season_price_per_night: number | null;
  image_urls: string[];
  facility_types: {
    name: string;
  };
}

interface FacilityNightRate {
  facility_id: string;
  night_number: number;
  price_per_night: number;
}

interface Activity {
  id: string;
  name: string;
  description: string | null;
  price_per_person: number;
  next_season_price_per_person: number | null;
  price_type: string | null;
  duration_minutes: number | null;
  max_participants: number | null;
  min_age: number | null;
  image_urls: string[];
  risk_assessment_url: string | null;
}

interface Booking {
  id: string;
  start_date: string;
  end_date: string;
  status: string;
  booking_facilities: {
    facility_id: string;
  }[];
}

interface FacilityRelationship {
  facility_id: string;
  linked_facility_id: string;
}

export default function TenantLanding({ subdomainSlug }: { subdomainSlug?: string }) {
  const { slug: paramSlug } = useParams<{ slug: string }>();
  // Use subdomain slug if provided, otherwise use URL param
  const slug = subdomainSlug || paramSlug;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [facilityRelationships, setFacilityRelationships] = useState<FacilityRelationship[]>([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [error, setError] = useState<string | null>(null);
  const [mapLatitude, setMapLatitude] = useState<number | null>(null);
  const [mapLongitude, setMapLongitude] = useState<number | null>(null);
  const [rateCode, setRateCode] = useState("");
  const [appliedRateTier, setAppliedRateTier] = useState<{ id: string; code: string; description: string | null; valid_from: string | null; valid_until: string | null } | null>(null);
  const [facilityRatePrices, setFacilityRatePrices] = useState<Record<string, number>>({});
  const [activityRatePrices, setActivityRatePrices] = useState<Record<string, number>>({});
  const [rateCodeError, setRateCodeError] = useState<string | null>(null);
  const [checkingRateCode, setCheckingRateCode] = useState(false);
  const rateCodeInputRef = useRef<HTMLInputElement>(null);
  const [showingNextSeason, setShowingNextSeason] = useState(false);
  const [facilityNightRates, setFacilityNightRates] = useState<Map<string, Record<number, number>>>(new Map());
  useEffect(() => {
    if (slug) {
      fetchTenantData();
    }
  }, [slug]);

  // Auto-apply rate code as user types (with debounce)
  useEffect(() => {
    if (!tenant || !rateCode.trim()) {
      if (!rateCode.trim() && appliedRateTier) {
        handleClearRateCode();
      }
      return;
    }

    const timer = setTimeout(() => {
      handleApplyRateCode();
    }, 500);

    return () => clearTimeout(timer);
  }, [rateCode, tenant]);

  useEffect(() => {
    if (tenant) {
      fetchCalendarData();
    }
  }, [tenant, currentMonth]);

  const fetchTenantData = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log("TenantLanding: fetching tenant for slug", slug);

      const { data: tenantData, error: tenantError } = await supabase
        .from("tenants")
        .select(`
          id,
          name,
          slug,
          general_settings (
            email_logo_url,
            address_line1,
            address_line2,
            city,
            postal_code,
            contact_phone,
            contact_email,
            landing_page_description,
            latitude,
            longitude,
            next_season_active_from,
            what3words_address
          )
        `)
        .eq("slug", slug)
        .eq("is_active", true)
        .maybeSingle();

      if (tenantError) throw tenantError;
      
      if (!tenantData) {
        console.log("TenantLanding: no tenant found for slug", slug);
        setTenant(null);
        setLoading(false);
        return;
      }
      
      console.log("TenantLanding: tenant loaded", tenantData);
      setTenant(tenantData);
      
      // Set map coordinates if available
      if (tenantData.general_settings?.[0]?.latitude && tenantData.general_settings?.[0]?.longitude) {
        setMapLatitude(tenantData.general_settings[0].latitude);
        setMapLongitude(tenantData.general_settings[0].longitude);
      }

      const { data: facilitiesData, error: facilitiesError } = await supabase
        .from("facilities")
        .select(`
          id,
          name,
          description,
          capacity,
          price_per_night,
          next_season_price_per_night,
          image_urls,
          facility_types (name)
        `)
        .eq("tenant_id", tenantData.id)
        .eq("is_active", true)
        .order("display_order", { ascending: true });

      if (facilitiesError) throw facilitiesError;
      setFacilities(facilitiesData || []);

      // Fetch night rates for all facilities
      const { data: nightRatesData, error: nightRatesError } = await supabase
        .from("facility_night_rates")
        .select("facility_id, night_number, price_per_night")
        .eq("tenant_id", tenantData.id);

      if (!nightRatesError && nightRatesData) {
        const ratesMap = new Map<string, Record<number, number>>();
        nightRatesData.forEach((rate: FacilityNightRate) => {
          if (!ratesMap.has(rate.facility_id)) {
            ratesMap.set(rate.facility_id, {});
          }
          ratesMap.get(rate.facility_id)![rate.night_number] = rate.price_per_night;
        });
        setFacilityNightRates(ratesMap);
      }

      const { data: activitiesData, error: activitiesError } = await supabase
        .from("activities")
        .select("*")
        .eq("tenant_id", tenantData.id)
        .eq("is_active", true)
        .order("display_order", { ascending: true });

      if (activitiesError) throw activitiesError;
      setActivities(activitiesData || []);

      const { data: relationshipsData, error: relationshipsError } = await supabase
        .from("facility_relationships")
        .select("facility_id, linked_facility_id");

      if (relationshipsError) throw relationshipsError;
      setFacilityRelationships(relationshipsData || []);
    } catch (error: any) {
      console.error("Error fetching tenant data:", error);
      setError("Failed to load campsite information");
      toast({
        title: "Error",
        description: "Failed to load campsite information",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCalendarData = async () => {
    if (!tenant) return;

    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);

    try {
      const { data, error } = await supabase
        .from("public_booking_availability")
        .select("booking_id, start_date, end_date, status, facility_id")
        .eq("tenant_id", tenant.id)
        .in("status", ["confirmed", "pending", "draft"])
        .gte("end_date", monthStart.toISOString().split("T")[0])
        .lte("start_date", monthEnd.toISOString().split("T")[0]);

      if (error) throw error;

      const groupedBookings = new Map<string, Booking>();
      (data || []).forEach((row) => {
        const existing = groupedBookings.get(row.booking_id);
        if (existing) {
          existing.booking_facilities.push({ facility_id: row.facility_id });
          return;
        }

        groupedBookings.set(row.booking_id, {
          id: row.booking_id,
          start_date: row.start_date,
          end_date: row.end_date,
          status: row.status,
          booking_facilities: [{ facility_id: row.facility_id }],
        });
      });

      setBookings(Array.from(groupedBookings.values()));
    } catch (error: any) {
      console.error("Error fetching bookings:", error);
    }
  };

  const handleApplyRateCode = async () => {
    if (!tenant || !rateCode.trim()) {
      setRateCodeError("Please enter a rate code");
      return;
    }

    setCheckingRateCode(true);
    setRateCodeError(null);

    try {
      // Look up the rate tier by code for this tenant
      const { data: rateTier, error: tierError } = await supabase
        .from("rate_tiers")
        .select("id, code, description, valid_from, valid_until")
        .eq("tenant_id", tenant.id)
        .eq("code", rateCode.trim().toUpperCase())
        .eq("is_active", true)
        .maybeSingle();

      if (tierError) throw tierError;

      if (!rateTier) {
        setRateCodeError("Invalid rate code");
        setAppliedRateTier(null);
        setFacilityRatePrices({});
        setActivityRatePrices({});
        return;
      }

      // Fetch facility rate prices for this tier
      const { data: facilityPrices, error: facilityError } = await supabase
        .from("facility_rate_prices")
        .select("facility_id, price_per_night")
        .eq("rate_tier_id", rateTier.id);

      if (facilityError) throw facilityError;

      // Fetch activity rate prices for this tier
      const { data: activityPrices, error: activityError } = await supabase
        .from("activity_rate_prices")
        .select("activity_id, price_per_person")
        .eq("rate_tier_id", rateTier.id);

      if (activityError) throw activityError;

      // Build lookup maps
      const facilityPriceMap: Record<string, number> = {};
      facilityPrices?.forEach((p) => {
        facilityPriceMap[p.facility_id] = p.price_per_night;
      });

      const activityPriceMap: Record<string, number> = {};
      activityPrices?.forEach((p) => {
        activityPriceMap[p.activity_id] = p.price_per_person;
      });

      setAppliedRateTier(rateTier);
      setFacilityRatePrices(facilityPriceMap);
      setActivityRatePrices(activityPriceMap);
      setRateCodeError(null);

      // Blur the input after successful application
      rateCodeInputRef.current?.blur();

      // Build message with date restrictions if any
      let message = `${rateTier.code} rate applied to prices`;
      if (rateTier.valid_from || rateTier.valid_until) {
        const from = rateTier.valid_from ? new Date(rateTier.valid_from).toLocaleDateString('en-GB') : 'Any';
        const until = rateTier.valid_until ? new Date(rateTier.valid_until).toLocaleDateString('en-GB') : 'Any';
        message += ` (valid for arrivals: ${from} - ${until})`;
      }

      toast({
        title: "Rate code applied",
        description: message,
      });
    } catch (error: any) {
      console.error("Error applying rate code:", error);
      setRateCodeError("Error checking rate code");
    } finally {
      setCheckingRateCode(false);
    }
  };

  const handleClearRateCode = () => {
    setRateCode("");
    setAppliedRateTier(null);
    setFacilityRatePrices({});
    setActivityRatePrices({});
    setRateCodeError(null);
  };

  const getFacilityPrice = (facility: Facility) => {
    // If rate tier is applied, use that price
    if (appliedRateTier && facilityRatePrices[facility.id] !== undefined) {
      return facilityRatePrices[facility.id];
    }
    // If showing next season and next season price exists, use that
    if (showingNextSeason && facility.next_season_price_per_night !== null) {
      return facility.next_season_price_per_night;
    }
    return facility.price_per_night;
  };

  const getActivityPrice = (activity: Activity) => {
    // If rate tier is applied, use that price
    if (appliedRateTier && activityRatePrices[activity.id] !== undefined) {
      return activityRatePrices[activity.id];
    }
    // If showing next season and next season price exists, use that
    if (showingNextSeason && activity.next_season_price_per_person !== null) {
      return activity.next_season_price_per_person;
    }
    return activity.price_per_person;
  };

  const getDaysInMonth = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    return eachDayOfInterval({ start: monthStart, end: monthEnd });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "confirmed":
        return "bg-green-500";
      case "pending":
        return "bg-orange-500";
      case "draft":
        return "bg-gray-400";
      default:
        return "bg-gray-300";
    }
  };

  const isDateBooked = (facilityId: string, date: Date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return bookings.some(
      (booking) =>
        booking.booking_facilities.some((bf) => bf.facility_id === facilityId) &&
        dateStr >= booking.start_date &&
        dateStr <= booking.end_date &&
        booking.status !== "draft"
    );
  };

  const isDateBlocked = (facilityId: string, date: Date) => {
    // Find facilities that, when booked, should block this facility
    // (i.e., where this facility is the linked_facility_id)
    const parentFacilities = facilityRelationships
      .filter((rel) => rel.linked_facility_id === facilityId)
      .map((rel) => rel.facility_id);

    return parentFacilities.some((parentId) => isDateBooked(parentId, date));
  };

  const allFacilityImages = facilities.flatMap((f) => f.image_urls || []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading campsite...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-2">Problem loading campsite</h1>
          <p className="text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-2">Campsite Not Found</h1>
          <p className="text-muted-foreground">The campsite you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  const settings = tenant.general_settings?.[0];

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background to-muted/20 overflow-x-hidden">
      {/* Header with Book Now */}
      <div className="sticky top-0 z-50 border-b bg-card/95 backdrop-blur-sm shadow-sm">
        <div className="container mx-auto px-4 py-4">
          {/* Desktop layout */}
          <div className="hidden md:flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="/favicon.png" 
                alt="SiteBook Logo" 
                className="w-12 h-12"
              />
              <h1 className="text-3xl font-bold text-black dark:text-white animate-fade-in">
                {tenant.name}
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <div className="relative">
                  {appliedRateTier ? (
                    <Check className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-green-600" />
                  ) : (
                    <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  )}
                  <Input
                    ref={rateCodeInputRef}
                    placeholder="RATE CODE"
                    value={rateCode}
                    onChange={(e) => {
                      setRateCode(e.target.value.toUpperCase());
                      setRateCodeError(null);
                    }}
                    className={`pl-9 pr-8 w-40 h-9 text-sm uppercase ${rateCodeError ? "border-destructive" : ""} ${appliedRateTier ? "border-green-500 bg-green-50 dark:bg-green-950/20" : ""}`}
                  />
                  {rateCode && (
                    <button
                      onClick={handleClearRateCode}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                {appliedRateTier && (appliedRateTier.valid_from || appliedRateTier.valid_until) && (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded">
                    <Calendar className="h-3 w-3" />
                    <span>
                      {appliedRateTier.valid_from ? new Date(appliedRateTier.valid_from).toLocaleDateString('en-GB') : 'Any'} - {appliedRateTier.valid_until ? new Date(appliedRateTier.valid_until).toLocaleDateString('en-GB') : 'Any'}
                    </span>
                  </div>
                )}
              </div>
              {/* Validity date box - only show when no rate code applied and next season date is set */}
              {!appliedRateTier && settings?.next_season_active_from && (
                <button
                  onClick={() => setShowingNextSeason(!showingNextSeason)}
                  className="flex items-center gap-2 px-3 py-2 text-sm border rounded-lg bg-muted/50 hover:bg-muted transition-colors cursor-pointer"
                >
                  <Calendar className="h-4 w-4 text-primary" />
                  {showingNextSeason ? (
                    <span className="text-accent font-medium">
                      Next Season From {format(new Date(settings.next_season_active_from), "dd/MM/yyyy")}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">
                      Valid till {format(subDays(new Date(settings.next_season_active_from), 1), "dd/MM/yyyy")}
                    </span>
                  )}
                </button>
              )}
              <Button
                size="lg" 
                onClick={() => navigate("/auth")} 
                className="bg-gradient-hero hover:scale-105 transition-transform shadow-lg"
              >
                <Calendar className="mr-2 h-5 w-5" />
                Book Now
              </Button>
              <Button 
                size="lg" 
                variant="ghost"
                onClick={() => navigate("/auth")} 
                className="hover:bg-muted/50 transition-colors"
              >
                <Users className="h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* Mobile layout */}
          <div className="flex flex-col md:hidden gap-3">
            {/* Logo and name centered */}
            <div className="flex items-center justify-center gap-3">
              <img 
                src="/favicon.png" 
                alt="SiteBook Logo" 
                className="w-10 h-10"
              />
              <h1 className="text-2xl font-bold text-black dark:text-white animate-fade-in">
                {tenant.name}
              </h1>
            </div>
            {/* Rate code, Valid button, Sign in stacked vertically */}
            <div className="flex flex-col gap-2">
              <div className="relative">
                {appliedRateTier ? (
                  <Check className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-green-600" />
                ) : (
                  <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                )}
                <Input
                  placeholder="RATE CODE"
                  value={rateCode}
                  onChange={(e) => {
                    setRateCode(e.target.value.toUpperCase());
                    setRateCodeError(null);
                  }}
                  className={`pl-9 pr-8 w-full h-9 text-sm uppercase ${rateCodeError ? "border-destructive" : ""} ${appliedRateTier ? "border-green-500 bg-green-50 dark:bg-green-950/20" : ""}`}
                />
                {rateCode && (
                  <button
                    onClick={handleClearRateCode}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              {/* Rate code validity dates - show when rate code is applied */}
              {appliedRateTier && (appliedRateTier.valid_from || appliedRateTier.valid_until) && (
                <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground bg-muted/50 px-2 py-2 rounded w-full">
                  <Calendar className="h-3 w-3" />
                  <span>
                    {appliedRateTier.valid_from ? new Date(appliedRateTier.valid_from).toLocaleDateString('en-GB') : 'Any'} - {appliedRateTier.valid_until ? new Date(appliedRateTier.valid_until).toLocaleDateString('en-GB') : 'Any'}
                  </span>
                </div>
              )}
              {/* Next season validity toggle - show when no rate code applied */}
              {!appliedRateTier && settings?.next_season_active_from && (
                <button
                  onClick={() => setShowingNextSeason(!showingNextSeason)}
                  className="flex items-center justify-center gap-2 px-3 py-2 text-sm border rounded-lg bg-muted/50 hover:bg-muted transition-colors cursor-pointer w-full"
                >
                  <Calendar className="h-4 w-4 text-primary" />
                  {showingNextSeason ? (
                    <span className="text-accent font-medium">
                      Next Season From {format(new Date(settings.next_season_active_from), "dd/MM/yyyy")}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">
                      Valid till {format(subDays(new Date(settings.next_season_active_from), 1), "dd/MM/yyyy")}
                    </span>
                  )}
                </button>
              )}
              <Button
                size="lg" 
                onClick={() => navigate("/auth")} 
                className="bg-gradient-hero hover:scale-105 transition-transform shadow-lg w-full"
              >
                <Calendar className="mr-2 h-5 w-5" />
                Book Now
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Carousel */}
      {allFacilityImages.length > 0 && (
        <div className="container mx-auto px-4 py-12 animate-fade-in">
          <Carousel className="w-full">
            <CarouselContent>
              {allFacilityImages.map((image, index) => (
                <CarouselItem key={index}>
                  <div className="relative h-[500px] w-full rounded-2xl overflow-hidden shadow-2xl group">
                    <img
                      src={image}
                      alt={`Campsite view ${index + 1}`}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                    {index === 0 && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center text-white drop-shadow-2xl">
                          <p className="text-2xl md:text-3xl font-light tracking-wider mb-2 opacity-90">
                            Welcome To
                          </p>
                          <h1 className="text-5xl md:text-7xl font-bold tracking-tight">
                            {tenant.name}
                          </h1>
                        </div>
                      </div>
                    )}
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="left-6 bg-card/90 backdrop-blur-sm hover:bg-card shadow-lg" />
            <CarouselNext className="right-6 bg-card/90 backdrop-blur-sm hover:bg-card shadow-lg" />
          </Carousel>
        </div>
      )}

      {/* Contact Info */}
      {settings?.contact_phone && (
        <div className="container mx-auto px-4 py-6">
          <div className="flex gap-6 justify-center flex-wrap">
            <a 
              href={`tel:${settings.contact_phone}`}
              className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors px-4 py-2 rounded-lg hover:bg-muted/50"
            >
              <Phone className="h-5 w-5" />
              <span className="font-medium">{settings.contact_phone}</span>
            </a>
          </div>
        </div>
      )}

      {/* Landing Page Description */}
      {settings?.landing_page_description && (
        <div className="container mx-auto px-4 py-8">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-lg text-muted-foreground leading-relaxed whitespace-pre-line">
              {settings.landing_page_description}
            </p>
          </div>
        </div>
      )}

      {/* Facilities Section */}
      <div className="container mx-auto px-4 py-16">
        <div className="text-center mb-12 animate-fade-in">
          <h2 className="text-4xl font-bold mb-3 bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
            Our Facilities
          </h2>
          <p className="text-muted-foreground text-lg">Discover the perfect accommodation for your stay</p>
        </div>
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {facilities.map((facility, index) => (
            <Card 
              key={facility.id} 
              className="group overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-2 hover:border-primary/50 animate-fade-in"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {facility.image_urls && facility.image_urls.length > 0 && (
                <div className="relative">
                  <Carousel className="w-full">
                    <CarouselContent>
                      {facility.image_urls.map((imageUrl, imgIndex) => (
                        <CarouselItem key={imgIndex}>
                          <div className="h-56 w-full overflow-hidden">
                            <img
                              src={imageUrl}
                              alt={`${facility.name} - Image ${imgIndex + 1}`}
                              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                            />
                          </div>
                        </CarouselItem>
                      ))}
                    </CarouselContent>
                    {facility.image_urls.length > 1 && (
                      <>
                        <CarouselPrevious className="left-2 bg-card/90 backdrop-blur-sm hover:bg-card shadow-lg" />
                        <CarouselNext className="right-2 bg-card/90 backdrop-blur-sm hover:bg-card shadow-lg" />
                      </>
                    )}
                  </Carousel>
                    {!facilityNightRates.has(facility.id) || Object.keys(facilityNightRates.get(facility.id)!).length === 0 ? (
                      <div className="absolute top-3 right-3 bg-primary/90 backdrop-blur-sm text-primary-foreground px-3 py-1 rounded-full text-sm font-semibold shadow-lg z-10">
                        £{getFacilityPrice(facility)}/night
                      </div>
                    ) : null}
                </div>
              )}
              <CardHeader>
                <CardTitle className="text-xl group-hover:text-primary transition-colors">{facility.name}</CardTitle>
                <CardDescription className="text-sm font-medium">{facility.facility_types?.name || 'Accommodation'}</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{facility.description}</p>
                <div className="flex items-center gap-2 text-sm bg-muted/50 rounded-lg px-3 py-2">
                  <Users className="h-4 w-4 text-primary" />
                  <span className="font-medium">Capacity: {facility.capacity}</span>
                </div>
                {facilityNightRates.has(facility.id) && Object.keys(facilityNightRates.get(facility.id)!).length > 0 && (
                  <div className="mt-3 border rounded-lg p-3 bg-muted/30">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Night Rates</p>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      {facilityNightRates.get(facility.id)![1] != null && (
                        <div>
                          <p className="text-xs text-muted-foreground">1st</p>
                          <p className="text-sm font-bold">£{facilityNightRates.get(facility.id)![1].toFixed(2)}</p>
                        </div>
                      )}
                      {facilityNightRates.get(facility.id)![2] != null && (
                        <div>
                          <p className="text-xs text-muted-foreground">2nd</p>
                          <p className="text-sm font-bold">£{facilityNightRates.get(facility.id)![2].toFixed(2)}</p>
                        </div>
                      )}
                      {facilityNightRates.get(facility.id)![3] != null && (
                        <div>
                          <p className="text-xs text-muted-foreground">3rd+</p>
                          <p className="text-sm font-bold">£{facilityNightRates.get(facility.id)![3].toFixed(2)}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Activities Section */}
      {activities.length > 0 && (
        <div className="container mx-auto px-4 py-16 bg-gradient-to-b from-muted/30 to-transparent">
          <div className="text-center mb-12 animate-fade-in">
            <h2 className="text-4xl font-bold mb-3 bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
              Activities & Experiences
            </h2>
            <p className="text-muted-foreground text-lg">Make unforgettable memories during your stay</p>
          </div>
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {activities.map((activity, index) => (
              <Card 
                key={activity.id}
                className="group overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-2 hover:border-primary/50 animate-fade-in"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {activity.image_urls && activity.image_urls.length > 0 && (
                  <div className="relative">
                    <Carousel className="w-full">
                      <CarouselContent>
                        {activity.image_urls.map((imageUrl, imgIndex) => (
                          <CarouselItem key={imgIndex}>
                            <div className="h-56 w-full overflow-hidden">
                              <img
                                src={imageUrl}
                                alt={`${activity.name} - Image ${imgIndex + 1}`}
                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                              />
                            </div>
                          </CarouselItem>
                        ))}
                      </CarouselContent>
                      {activity.image_urls.length > 1 && (
                        <>
                          <CarouselPrevious className="left-2 bg-card/90 backdrop-blur-sm hover:bg-card shadow-lg" />
                          <CarouselNext className="right-2 bg-card/90 backdrop-blur-sm hover:bg-card shadow-lg" />
                        </>
                      )}
                    </Carousel>
                    <div className="absolute top-3 right-3 bg-primary/90 backdrop-blur-sm text-primary-foreground px-3 py-1 rounded-full text-sm font-semibold shadow-lg z-10">
                      £{getActivityPrice(activity)}{activity.price_type === "per_hour" ? "/hr" : "/pp"}
                    </div>
                  </div>
                )}
                <CardHeader>
                  <CardTitle className="text-xl group-hover:text-primary transition-colors">{activity.name}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{activity.description}</p>
                  <div className="space-y-2">
                    {activity.duration_minutes && (
                      <div className="flex items-center gap-2 text-sm bg-muted/50 rounded-lg px-3 py-2">
                        <Clock className="h-4 w-4 text-primary" />
                        <span className="font-medium">{activity.duration_minutes} minutes</span>
                      </div>
                    )}
                    {activity.max_participants && (
                      <div className="flex items-center gap-2 text-sm bg-muted/50 rounded-lg px-3 py-2">
                        <Users className="h-4 w-4 text-primary" />
                        <span className="font-medium">Up to {activity.max_participants} people</span>
                      </div>
                    )}
                    {activity.min_age ? (
                      <div className="text-sm text-muted-foreground px-3 pt-1">Ages {activity.min_age}+</div>
                    ) : null}


                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Availability Calendar */}
      <div className="container mx-auto px-4 py-16 pb-20">
        <div className="text-center mb-12 animate-fade-in">
          <h2 className="text-4xl font-bold mb-3 bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
            Facility Availability
          </h2>
          <p className="text-muted-foreground text-lg">View which facilities are booked or have pending bookings</p>
        </div>

          <div className="bg-card rounded-lg border p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold">Timeline Calendar</h3>
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() - 1)))}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="px-4 py-2 border rounded-md min-w-[180px] text-center">
                <span className="font-medium">{format(currentMonth, "MMMM yyyy")}</span>
              </div>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() + 1)))}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="border rounded-lg overflow-hidden">
            <div className="relative">
              <div className="overflow-x-auto">
                <div className="inline-block min-w-full">
                  {/* Header Row */}
                  <div className="flex border-b bg-muted/30">
                    <div className="sticky left-0 w-32 md:w-64 flex-shrink-0 p-2 md:p-3 font-semibold text-sm md:text-base border-r bg-background/80 backdrop-blur-md z-20">Facilities</div>
                    {getDaysInMonth().map((day) => (
                      <div
                        key={day.toISOString()}
                        className={`w-10 md:w-16 flex-shrink-0 p-1 md:p-2 text-center text-[10px] md:text-xs border-r ${
                          isWeekend(day) ? "bg-muted/50" : "bg-background"
                        }`}
                      >
                        <div className="font-medium">{format(day, "d")}</div>
                        <div className="text-muted-foreground text-[8px] md:text-[10px]">{format(day, "EEE")}</div>
                      </div>
                    ))}
                  </div>

                  {/* Facility Rows */}
                  {facilities.map((facility) => {
                    const bookedDays = getDaysInMonth().filter(day => isDateBooked(facility.id, day));
                    const blockedDays = getDaysInMonth().filter(day => isDateBlocked(facility.id, day));
                    
                    return (
                      <div key={facility.id} className="flex border-b hover:bg-muted/20 transition-colors">
                        <div className="sticky left-0 w-32 md:w-64 flex-shrink-0 p-2 md:p-3 border-r bg-background/80 backdrop-blur-md z-20">
                          <div className="flex items-center gap-2 md:gap-3">
                            {facility.image_urls && facility.image_urls.length > 0 && (
                              <img
                                src={facility.image_urls[0]}
                                alt={facility.name}
                                className="w-8 h-8 md:w-12 md:h-12 rounded object-cover flex-shrink-0"
                              />
                            )}
                            <div className="min-w-0">
                              <div className="font-medium text-xs md:text-sm leading-tight truncate">{facility.name}</div>
                              <div className="text-[10px] md:text-xs text-muted-foreground truncate">{facility.facility_types?.name || 'Accommodation'}</div>
                            </div>
                          </div>
                        </div>
                        {getDaysInMonth().map((day) => {
                          const isBooked = isDateBooked(facility.id, day);
                          const isBlocked = isDateBlocked(facility.id, day);
                          const booking = bookings.find(b => 
                            b.booking_facilities.some(bf => bf.facility_id === facility.id) &&
                            format(day, "yyyy-MM-dd") >= b.start_date &&
                            format(day, "yyyy-MM-dd") <= b.end_date &&
                            b.status !== "draft"
                          );

                          return (
                            <div
                              key={day.toISOString()}
                              className={`w-10 md:w-16 flex-shrink-0 p-1 md:p-2 border-r flex items-center justify-center ${
                                isWeekend(day) ? "bg-muted/50" : "bg-background"
                              }`}
                            >
                              {booking?.status === "confirmed" && (
                                <div className="w-full h-6 md:h-8 bg-green-500 rounded" title="Confirmed Booking" />
                              )}
                              {booking?.status === "pending" && (
                                <div className="w-full h-6 md:h-8 bg-orange-500 rounded" title="Pending Booking" />
                              )}
                              {!booking && isBlocked && (
                                <div className="w-full h-6 md:h-8 bg-gray-300 rounded" title="Blocked" />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-center gap-6 mt-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-green-500 rounded" />
            <span>Confirmed Booking</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-orange-500 rounded" />
            <span>Pending Booking</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-gray-300 rounded" />
            <span>Blocked</span>
          </div>
        </div>

        {/* Contact Details Section */}
        {settings && (settings.contact_phone || settings.contact_email || settings.address_line1) && (
          <div className="container mx-auto px-4 py-16 bg-gradient-to-b from-muted/30 to-transparent">
            <div className="text-center mb-12 animate-fade-in">
              <h2 className="text-4xl font-bold mb-3 bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                Our Contact Details
              </h2>
              <p className="text-muted-foreground text-lg">Get in touch with us</p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {/* Left Column: Contact Information + Message Form */}
              <div className="space-y-8">
                {/* Contact Information Card */}
                <Card className="shadow-lg hover:shadow-xl transition-shadow">
                  <CardHeader>
                    <CardTitle className="text-2xl">{tenant.name}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {settings.address_line1 && (
                      <div className="flex items-start gap-3">
                        <MapPin className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                        <div className="text-muted-foreground">
                          {settings.address_line1}
                          {settings.address_line2 && <><br />{settings.address_line2}</>}
                          {settings.city && <><br />{settings.city}</>}
                          {settings.postal_code && <><br />{settings.postal_code}</>}
                        </div>
                      </div>
                    )}

                    {settings.what3words_address && (
                      <div className="flex items-center gap-3">
                        <span className="h-5 w-5 text-primary flex-shrink-0 font-bold text-sm flex items-center justify-center">///</span>
                        <a 
                          href={`https://what3words.com/${settings.what3words_address.replace(/^\/\/\//, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-muted-foreground hover:text-primary transition-colors font-medium"
                        >
                          {settings.what3words_address.replace(/^\/\/\//, '')}
                        </a>
                      </div>
                    )}
                    
                    {settings.contact_phone && (
                      <div className="flex items-center gap-3">
                        <Phone className="h-5 w-5 text-primary flex-shrink-0" />
                        <a 
                          href={`tel:${settings.contact_phone}`}
                          className="text-muted-foreground hover:text-primary transition-colors"
                        >
                          {settings.contact_phone}
                        </a>
                      </div>
                    )}
                    
                    {settings.contact_email && (
                      <div className="flex items-center gap-3">
                        <Mail className="h-5 w-5 text-primary flex-shrink-0" />
                        <a 
                          href={`mailto:${settings.contact_email}`}
                          className="text-muted-foreground hover:text-primary transition-colors break-all"
                        >
                          {settings.contact_email}
                        </a>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Quick Send Message Card */}
                <Card className="shadow-lg hover:shadow-xl transition-shadow">
                  <CardHeader>
                    <CardTitle className="text-xl">Send us a Message</CardTitle>
                    <CardDescription>We'll get back to you as soon as possible</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form className="space-y-4" onSubmit={async (e) => {
                      e.preventDefault();
                      const formData = new FormData(e.currentTarget);
                      const name = (formData.get("name") as string).trim();
                      const email = (formData.get("email") as string).trim();
                      const phone = (formData.get("phone") as string).trim();
                      const message = (formData.get("message") as string).trim();
                      const honeypot = formData.get("website") as string;

                      // Client-side validation
                      if (name.length > 100) {
                        toast({
                          title: "Error",
                          description: "Name must be less than 100 characters.",
                        });
                        return;
                      }

                      if (message.length > 2000) {
                        toast({
                          title: "Error",
                          description: "Message must be less than 2000 characters.",
                        });
                        return;
                      }

                      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                      if (!emailRegex.test(email)) {
                        toast({
                          title: "Error",
                          description: "Please enter a valid email address.",
                        });
                        return;
                      }

                      if (!settings?.contact_email) {
                        toast({
                          title: "Error",
                          description: "Contact email is not configured for this site.",
                        });
                        return;
                      }

                      // Disable submit button to prevent double submission
                      const submitButton = e.currentTarget.querySelector('button[type="submit"]') as HTMLButtonElement;
                      if (submitButton) {
                        submitButton.disabled = true;
                        submitButton.textContent = "Sending...";
                      }

                      try {
                        const { data, error } = await supabase.functions.invoke("send-contact-message", {
                          body: {
                            name,
                            email,
                            phone,
                            message,
                            tenantName: tenant.name,
                            recipientEmail: settings.contact_email,
                            honeypot,
                            tenantLogoUrl: settings.email_logo_url,
                          },
                        });

                        if (error) throw error;

                        toast({
                          title: "Message sent!",
                          description: "We'll get back to you as soon as possible.",
                        });
                        e.currentTarget.reset();
                      } catch (error: any) {
                        console.error("Error sending message:", error);
                        
                        // Handle rate limiting error
                        if (error.message?.includes("429") || error.message?.includes("Too many")) {
                          toast({
                            title: "Too many requests",
                            description: "You've sent too many messages. Please try again later.",
                          });
                        } else {
                          toast({
                            title: "Error",
                            description: "Failed to send message. Please try again.",
                          });
                        }
                      } finally {
                        // Re-enable submit button
                        if (submitButton) {
                          submitButton.disabled = false;
                          submitButton.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="mr-2 h-4 w-4 inline"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>Send Message';
                        }
                      }
                    }}>
                      {/* Honeypot field - hidden from users but visible to bots */}
                      <div style={{ position: "absolute", left: "-5000px" }} aria-hidden="true">
                        <input
                          type="text"
                          name="website"
                          tabIndex={-1}
                          autoComplete="off"
                        />
                      </div>
                      
                      <div>
                        <input
                          type="text"
                          name="name"
                          placeholder="Your Name"
                          required
                          maxLength={100}
                          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>
                      <div>
                        <input
                          type="email"
                          name="email"
                          placeholder="Your Email"
                          required
                          maxLength={255}
                          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>
                      <div>
                        <input
                          type="tel"
                          name="phone"
                          placeholder="Your Contact Number"
                          required
                          maxLength={20}
                          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>
                      <div>
                        <textarea
                          name="message"
                          placeholder="Your Message"
                          required
                          rows={4}
                          maxLength={2000}
                          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                        />
                        <p className="text-xs text-muted-foreground mt-1">Maximum 2000 characters</p>
                      </div>
                      <Button type="submit" className="w-full bg-gradient-hero">
                        <Mail className="mr-2 h-4 w-4" />
                        Send Message
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </div>

              {/* Right Column: Map */}
              <div className="h-full min-h-[600px] rounded-lg overflow-hidden shadow-lg">
                <MapEmbed
                  address={settings.address_line1 || undefined}
                  city={settings.city || undefined}
                  postalCode={settings.postal_code || undefined}
                  latitude={mapLatitude}
                  longitude={mapLongitude}
                  className="h-full"
                />
              </div>
            </div>
          </div>
        )}

        {/* Guest Reviews Section */}
        <PublicReviewsSection tenantId={tenant.id} />

        {/* CTA Section */}
        <div className="mt-16 text-center bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 rounded-2xl p-12 border-2 border-primary/20 shadow-xl">
          <h3 className="text-3xl font-bold mb-4">Ready to Book Your Adventure?</h3>
          <p className="text-muted-foreground text-lg mb-6">Join us for an unforgettable experience</p>
          <Button 
            size="lg" 
            onClick={() => navigate("/auth")}
            className="bg-gradient-hero hover:scale-105 transition-transform shadow-lg text-lg px-8 py-6"
          >
            <Calendar className="mr-2 h-6 w-6" />
            Start Your Booking
          </Button>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-card border-t mt-20">
        <div className="container mx-auto px-4 py-12">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* SiteBook Branding */}
            <div className="space-y-4">
              <a 
                href="https://sitebook.uk" 
                className="flex items-center gap-3 hover:opacity-80 transition-opacity w-fit"
              >
                <div className="bg-gradient-hero p-3 rounded-xl">
                  <Tent className="h-8 w-8 text-primary-foreground" />
                </div>
                <h3 className="text-2xl font-bold text-foreground">
                  SiteBook
                </h3>
              </a>
              <p className="text-muted-foreground max-w-md">
                The complete booking solution for campsites, activity centres, and outdoor venues. 
                Streamline your operations and delight your guests.
              </p>
            </div>

            {/* CTA */}
            <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl p-8 border-2 border-primary/20">
              <h4 className="text-2xl font-bold mb-3">Join SiteBook</h4>
              <p className="text-muted-foreground mb-6">
                Are you a site or venue owner? List your venue on SiteBook and manage and reach thousands of potential guests.
              </p>
              <Button 
                size="lg" 
                onClick={() => {
                  window.location.href = "https://sitebook.uk/join";
                }}
                className="bg-gradient-hero hover:scale-105 transition-transform shadow-lg w-full"
              >
                Get Started Today
              </Button>
            </div>
          </div>

          {/* Copyright */}
          <div className="mt-8 pt-8 border-t text-center text-sm text-muted-foreground">
            <p>&copy; {new Date().getFullYear()} SiteBook. All rights reserved.</p>
            <p className="text-xs text-muted-foreground/70 mt-2">
              Trakto Software Ltd - Registered in England & Wales 16886380 - 20-22 Wenlock Road, London, N1 7GU
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
